import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  MultiSelect,
  NumberInput,
  ScrollArea,
  Select,
  Slider,
  Stack,
  Switch,
  Tabs,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import {
  IconArrowsRightLeft,
  IconBook,
  IconClock,
  IconExternalLink,
  IconHistory,
  IconLanguage,
  IconMicrophone,
  IconPhone,
  IconPhoneOff,
  IconPlus,
  IconRobot,
  IconSparkles,
  IconTool,
  IconTrash,
} from '@tabler/icons-react';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getRobot, updateRobotConfigs, getIntegrations, RobotRequestConfigs, IntegrationItem } from '@mygpt/app/apis';
import { createPromptVersion, listPromptVersions } from '@mygpt/app/apis/services/prompt-versions';
import { useBot } from '@mygpt/app/bot';
import { OPENAI_LIVE_ADDITIONAL_VOICE_OPTIONS, resolveRealtimeVoice } from '@mygpt/app/chat/types/realtime';
import { VersionHistoryDrawer } from '@/components/Settings/items/PromptOptimization/VersionHistoryDrawer';

import ContainerHeader from '@/components/Headers/ContainerHeader';
import { useGPTStore } from '@/store';
import CallLog from './CallLog';
import { DEFAULT_LIVE_VOICE_PROMPT, DEFAULT_PHONE_PROMPT } from './defaultPrompt';

type PhoneRoute = {
  id?: string;
  label: string; // 转接给（负责人/部门名）
  number: string; // 直拨号码 E.164
  intent_examples: string[]; // 关键词（可选，给模型判定提示）
  description: string; // 什么情况下转给TA（自由文本，可选）
  enabled: boolean;
};
type PhoneDefaultRoute = { label: string; number: string; enabled: boolean }; // 默认转接（带启用开关）
type PhoneTransfer = {
  enabled: boolean;
  announcement: string; // 转送前案内模板，{部署名} 自动填充
  routes: PhoneRoute[];
  default_route: PhoneDefaultRoute; // 空(label/number 均空) 或 enabled=false → 等于无默认转接
  caller_id: string;
  no_answer_message: string;
};
// 同時通訳（双腿翻译桥）。voice_to_* / verbatim_entities / subtitle 为后端预留字段，
// v1 UI 不露出（translate 端点暂无对应能力/字幕二期），读写时原样保留。
type PhoneInterpreter = {
  enabled: boolean;
  agent_number: string; // 坐席电话 E.164
  agent_language: string; // 坐席语言（13 语白名单）
  no_answer_message: string;
  customer_language_candidates: string[];
  customer_language_default: string; // 客户语言无法判定时的兜底（判定前假定语言）
  greeting_language: string; // 开场白播报语言
  original_audio: { enabled: boolean; volume: number };
  greeting: string;
};

interface PhoneFormValues {
  phone_enabled: boolean;
  phone_prompt: string;
  phone_greeting: string;
  phone_language_default: string;
  phone_farewell: string;
  phone_sales_email: string;
  phone_model: string;
  phone_live_voice_prompt: string; // gpt-live-1 音声モデル用（音声・ツールタブ、保存バー経由）
  phone_voice: string;
  phone_effort: string;
  phone_call_mode: 'ai' | 'transfer' | 'interpreter';
  phone_vad: { type: string; eagerness: string };
  phone_tools: {
    knowledge: { enabled: boolean; top_k: number };
    end_call: { enabled: boolean };
    transfer: { enabled: boolean; number: string };
    booking: { enabled: boolean };
  };
  phone_recording: boolean;
  phone_post_call: { enabled: boolean; presets: { lark_notify: boolean }; instruction: string };
  phone_transfer: PhoneTransfer;
  phone_interpreter: PhoneInterpreter;
}

// 音声モデル。新增模型时在此追加（后端 robots.py PHONE_MODEL_ALLOWED 与 realtime phone-config.js 同步）。
const PHONE_MODEL_OPTIONS = ['gpt-realtime-2', 'gpt-live-1'];
const PHONE_MODEL_DEFAULT = 'gpt-realtime-2';
// 全二重（GPT-Live 系）模型：轮次检测由模型自理、无 reasoning effort → 隐藏「推論強度」「応答の積極性」
const PHONE_LIVE_MODELS = new Set(['gpt-live-1']);
const VOICE_OPTIONS = ['marin', 'cedar', 'alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse'];
const EFFORT_OPTIONS = ['minimal', 'low', 'medium'];
const EAGERNESS_OPTIONS = ['low', 'medium', 'high', 'auto'];
// 同時通訳对应的 13 语输出白名单（与后端校验一致）。标签用各语言母语名，无需 i18n。
const INTERPRETER_LANG_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: '中文' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'ru', label: 'Русский' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'vi', label: 'Tiếng Việt' },
];

const VIOLET = '#7C68FB';
const VIOLET_SOFT = 'rgba(124, 104, 251, 0.10)';
const VIOLET_SOFTER = 'rgba(124, 104, 251, 0.05)';
const CARD_SHADOW = '0px 10px 60px 0px rgba(226, 236, 249, 0.50)';
// 转接列表的统一网格：担当・部署(封顶宽) | 電話番号 | 触发条件预览(弹性填充) | 有効(开关) | 删除
const TRANSFER_GRID = 'minmax(180px, 360px) 170px minmax(0, 1fr) 48px 32px';

const DEFAULT_VALUES: PhoneFormValues = {
  phone_enabled: false,
  phone_prompt: '',
  phone_greeting: '',
  phone_language_default: 'ja',
  phone_farewell: '',
  phone_sales_email: '',
  phone_model: PHONE_MODEL_DEFAULT,
  phone_live_voice_prompt: '',
  phone_voice: 'marin',
  phone_effort: 'low',
  phone_call_mode: 'ai',
  phone_vad: { type: 'semantic_vad', eagerness: 'low' },
  phone_tools: {
    knowledge: { enabled: true, top_k: 5 },
    end_call: { enabled: true },
    transfer: { enabled: false, number: '' },
    booking: { enabled: false },
  },
  phone_recording: false,
  phone_post_call: { enabled: false, presets: { lark_notify: false }, instruction: '' },
  phone_transfer: {
    enabled: false,
    announcement: '',
    routes: [],
    default_route: { label: '', number: '', enabled: true },
    caller_id: '',
    no_answer_message: '',
  },
  phone_interpreter: {
    enabled: false,
    agent_number: '',
    agent_language: 'ja',
    no_answer_message: '',
    customer_language_candidates: [],
    customer_language_default: 'en',
    greeting_language: 'en',
    original_audio: { enabled: true, volume: 0.15 },
    greeting: '',
  },
};

/* 区块卡片：白底圆角 + 项目统一阴影 + 图标标题 */
const SectionCard = ({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) => (
  <div className="rounded-xl bg-white p-6" style={{ boxShadow: CARD_SHADOW }}>
    <Group gap={10} mb="md">
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 9,
          background: VIOLET_SOFT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </div>
      <Text fw={600}>{title}</Text>
    </Group>
    {children}
  </div>
);

/* 设置行：标题 + 描述 + 右侧控件 */
const SettingRow = ({
  title,
  description,
  control,
  first,
  badge,
  disabled,
}: {
  title: string;
  description?: string;
  control: ReactNode;
  first?: boolean;
  badge?: ReactNode;
  disabled?: boolean;
}) => (
  <Group
    justify="space-between"
    align="center"
    wrap="nowrap"
    py={12}
    style={{ borderTop: first ? 'none' : '1px solid #F1F2F6', opacity: disabled ? 0.55 : 1 }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <Group gap={8}>
        <Text size="sm" fw={600}>
          {title}
        </Text>
        {badge}
      </Group>
      {description && (
        <Text size="xs" c="gray.6" mt={2}>
          {description}
        </Text>
      )}
    </div>
    {control}
  </Group>
);

const Phone = () => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const navigate = useNavigate();
  const robotInfo = useGPTStore((state) => state.robotInfo);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [twilioConfig, setTwilioConfig] = useState<IntegrationItem | null>(null);
  const [larkConfig, setLarkConfig] = useState<IntegrationItem | null>(null);
  // 电话 prompt 版本管理（与 realtime prompt 同套路）
  const [savedPhonePrompt, setSavedPhonePrompt] = useState('');
  const [promptVersionCount, setPromptVersionCount] = useState(0);
  const [historyOpened, setHistoryOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('basic');
  // 转接列表：当前打开「详情」弹框的行 index（纯 UI 状态，不入库；null=未打开）
  const [detailIdx, setDetailIdx] = useState<number | null>(null);
  // 文本字段（手动保存）的已保存快照，用于"未保存"判定 + 离开拦截。
  // 开关/下拉是即时保存的，不在此列；nested 的 instruction 在此（自由文本）。
  const [savedText, setSavedText] = useState({ p: '', g: '', f: '', e: '', i: '', t: '', n: '', l: '' });

  const form = useForm<PhoneFormValues>({ initialValues: DEFAULT_VALUES });
  // phone_interpreter 后端是整键替换：保存时把 UI 未管理的预留字段
  // （voice_to_*/verbatim_entities/subtitle 等）原样回传，避免被 v1 保存抹掉
  const rawInterpRef = useRef<Record<string, unknown>>({});

  const textSnap = (v: PhoneFormValues) => ({
    p: v.phone_prompt,
    g: v.phone_greeting,
    f: v.phone_farewell,
    e: v.phone_sales_email,
    i: v.phone_post_call.instruction,
    t: JSON.stringify(v.phone_transfer), // 转人工路由表也纳入"未保存"判定 + 全局保存
    n: JSON.stringify({ ip: v.phone_interpreter, cm: v.phone_call_mode }), // 同時通訳 + 着信モード
    l: v.phone_live_voice_prompt, // gpt-live-1 音声モデル用プロンプト
  });
  const textDirty = JSON.stringify(textSnap(form.values)) !== JSON.stringify(savedText);

  // 即时保存（开关/下拉用）：合并进表单 + 单 key 落库 + 轻提示
  const commit = async (patch: Partial<RobotRequestConfigs>) => {
    form.setValues((cur) => ({ ...cur, ...patch } as PhoneFormValues));
    // phone_post_call 即时保存时其 instruction 文本也一并落库 → 同步快照，避免误报"未保存"
    if ('phone_post_call' in patch) {
      setSavedText((s) => ({ ...s, i: (patch as PhoneFormValues).phone_post_call?.instruction ?? s.i }));
    }
    if (!botId) return;
    try {
      await updateRobotConfigs({ aiId: botId, configs: patch });
      showNotification({ message: t('save_success'), color: 'green' });
    } catch (error) {
      console.error('即时保存失败:', error);
      showNotification({ message: t('phone.save_error'), color: 'red' });
    }
  };

  useEffect(() => {
    if (!botId) return;
    const load = async () => {
      try {
        setLoading(true);
        const [{ data }, integrationsRes] = await Promise.all([getRobot({ aiId: botId }), getIntegrations(botId)]);
        const c = data.configs || {};
        const ptRaw = c.phone_transfer || {};
        const phoneTransfer: PhoneTransfer = {
          enabled: ptRaw.enabled ?? false,
          announcement: ptRaw.announcement ?? '',
          routes: (ptRaw.routes ?? []).map(
            (r: {
              id?: string;
              label?: string;
              number?: string;
              intent_examples?: string[];
              description?: string;
              enabled?: boolean;
            }) => ({
              id: r.id || '',
              label: r.label || '',
              number: r.number || '',
              intent_examples: r.intent_examples ?? [],
              description: r.description ?? '',
              enabled: r.enabled ?? true,
            })
          ),
          default_route: {
            label: ptRaw.default_route?.label ?? '',
            number: ptRaw.default_route?.number ?? '',
            enabled: ptRaw.default_route?.enabled ?? true, // 旧配置(无 enabled) → 默认启用
          },
          caller_id: ptRaw.caller_id ?? '',
          no_answer_message: ptRaw.no_answer_message ?? '',
        };
        const piRaw = c.phone_interpreter || {};
        rawInterpRef.current = piRaw;
        const phoneInterpreter: PhoneInterpreter = {
          enabled: piRaw.enabled ?? false,
          agent_number: piRaw.agent_number ?? '',
          agent_language: piRaw.agent_language || 'ja',
          no_answer_message: piRaw.no_answer_message ?? '',
          customer_language_candidates: piRaw.customer_language_candidates ?? [],
          customer_language_default: piRaw.customer_language_default || 'en',
          // 旧配置无 greeting_language 时沿用兜底语言（与后端组装一致），行为不变
          greeting_language: piRaw.greeting_language || piRaw.customer_language_default || 'en',
          original_audio: {
            enabled: piRaw.original_audio?.enabled ?? true,
            volume: piRaw.original_audio?.volume ?? 0.15,
          },
          greeting: piRaw.greeting ?? '',
        };
        form.setValues({
          phone_enabled: c.phone_enabled ?? DEFAULT_VALUES.phone_enabled,
          phone_prompt: c.phone_prompt ?? '',
          phone_greeting: c.phone_greeting ?? '',
          phone_language_default: c.phone_language_default || 'ja',
          phone_farewell: c.phone_farewell ?? '',
          phone_sales_email: c.phone_sales_email ?? '',
          // 旧 bot 无 phone_model 行 → 显示默认 gpt-realtime-2（不触发保存，行为与改造前一致）
          phone_model: c.phone_model || PHONE_MODEL_DEFAULT,
          phone_live_voice_prompt: c.phone_live_voice_prompt ?? '',
          phone_voice: c.phone_voice || 'marin',
          phone_effort: c.phone_effort || 'low',
          phone_call_mode: c.phone_call_mode || 'ai',
          phone_vad: {
            type: c.phone_vad?.type || 'semantic_vad',
            eagerness: c.phone_vad?.eagerness || 'low',
          },
          phone_tools: {
            knowledge: {
              enabled: c.phone_tools?.knowledge?.enabled ?? true,
              top_k: c.phone_tools?.knowledge?.top_k ?? 5,
            },
            end_call: { enabled: c.phone_tools?.end_call?.enabled ?? true },
            transfer: {
              enabled: c.phone_tools?.transfer?.enabled ?? false,
              number: c.phone_tools?.transfer?.number || '',
            },
            booking: { enabled: c.phone_tools?.booking?.enabled ?? false },
          },
          phone_recording: c.phone_recording ?? false,
          phone_post_call: {
            enabled: c.phone_post_call?.enabled ?? false,
            presets: { lark_notify: c.phone_post_call?.presets?.lark_notify ?? false },
            instruction: c.phone_post_call?.instruction || '',
          },
          phone_transfer: phoneTransfer,
          phone_interpreter: phoneInterpreter,
        });
        setSavedPhonePrompt(c.phone_prompt ?? '');
        setSavedText({
          p: c.phone_prompt ?? '',
          g: c.phone_greeting ?? '',
          f: c.phone_farewell ?? '',
          e: c.phone_sales_email ?? '',
          i: c.phone_post_call?.instruction ?? '',
          t: JSON.stringify(phoneTransfer),
          n: JSON.stringify({ ip: phoneInterpreter, cm: c.phone_call_mode || 'ai' }),
          l: c.phone_live_voice_prompt ?? '',
        });
        const integrationList = integrationsRes.data.integrations || [];
        setTwilioConfig(integrationList.find((i) => i.platform === 'twilio') || null);
        setLarkConfig(integrationList.find((i) => i.platform === 'lark') || null);
      } catch (error) {
        console.error('加载电话受付设置失败:', error);
        showNotification({ message: t('phone.load_error'), color: 'red' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [botId]);

  // 电话 prompt 版本数（话术 tab 历史按钮显示用）
  const fetchPromptVersionCount = useCallback(async () => {
    if (!botId) return;
    try {
      const res = await listPromptVersions(botId, 'phone', 1, 1);
      setPromptVersionCount(res.total);
    } catch {
      // ignore
    }
  }, [botId]);

  useEffect(() => {
    fetchPromptVersionCount();
  }, [fetchPromptVersionCount]);

  // 文本字段有未保存改动时，刷新/关闭页面前提示（页面内切 tab 不丢；切菜单暂无法硬拦，靠提示+即时保存兜底）
  useEffect(() => {
    if (!textDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [textDirty]);

  // 版本回滚后：重新拉取 prompt 同步到表单
  const handleVersionActivated = async () => {
    if (!botId) return;
    try {
      const { data } = await getRobot({ aiId: botId });
      const val = data.configs?.phone_prompt || '';
      form.setFieldValue('phone_prompt', val);
      setSavedPhonePrompt(val);
    } catch {
      // ignore
    }
    fetchPromptVersionCount();
  };

  // 主开关即时保存（与 Realtime 页的开关交互一致）
  const handleEnabledChange = async (value: boolean) => {
    form.setFieldValue('phone_enabled', value);
    if (!botId) return;
    try {
      await updateRobotConfigs({ aiId: botId, configs: { phone_enabled: value } }, { skipErrorNotification: true });
      showNotification({ message: t('save_success'), color: 'green' });
    } catch (error) {
      console.error('保存电话受付开关失败:', error);
      form.setFieldValue('phone_enabled', !value);
      // 号码互斥等后端错误：优先展示已本地化的后端提示（含「该号码已被其它 bot 开启」），
      // 否则回退通用失败文案
      const msg = (error as { errorMsg?: string })?.errorMsg;
      showNotification({ message: msg || t('phone.save_error'), color: 'red' });
    }
  };

  const handleSave = async () => {
    if (!botId) return;
    try {
      setSaving(true);
      const v = form.values;
      // 转人工保存前清洗：丢弃完全空白的 route 行。default_route 原样发送：
      // label/number 均空 或 enabled=false → 后端与 bridge 都当作"无默认转接"。
      const ptForm = v.phone_transfer;
      const cleanedTransfer: PhoneTransfer = {
        ...ptForm,
        routes: ptForm.routes.filter((r) => (r.label || '').trim() || (r.number || '').trim()),
      };
      const configs: Partial<RobotRequestConfigs> = {
        phone_prompt: v.phone_prompt,
        phone_greeting: v.phone_greeting,
        phone_language_default: v.phone_language_default,
        phone_farewell: v.phone_farewell,
        phone_sales_email: v.phone_sales_email,
        phone_model: v.phone_model,
        phone_live_voice_prompt: v.phone_live_voice_prompt,
        phone_voice: v.phone_voice,
        phone_effort: v.phone_effort,
        phone_call_mode: v.phone_call_mode,
        phone_vad: v.phone_vad,
        phone_tools: v.phone_tools,
        phone_recording: v.phone_recording,
        phone_post_call: v.phone_post_call,
        phone_transfer: cleanedTransfer,
        // 同時通訳：预留字段原样回传（后端整键替换）+ 坐席号去空格/连字符
        phone_interpreter: {
          ...rawInterpRef.current,
          ...v.phone_interpreter,
          agent_number: (v.phone_interpreter.agent_number || '').replace(/[\s\-()]/g, ''),
        },
      };
      await updateRobotConfigs({ aiId: botId, configs });
      form.setFieldValue('phone_transfer', cleanedTransfer);
      const cleanedInterp = {
        ...v.phone_interpreter,
        agent_number: (v.phone_interpreter.agent_number || '').replace(/[\s\-()]/g, ''),
      };
      form.setFieldValue('phone_interpreter', cleanedInterp);
      setSavedText(textSnap({ ...v, phone_transfer: cleanedTransfer, phone_interpreter: cleanedInterp }));
      // prompt 有变更时记录一个新版本（与 realtime prompt 同套路）
      if (v.phone_prompt !== savedPhonePrompt) {
        try {
          await createPromptVersion(botId, { prompt: v.phone_prompt, prompt_type: 'phone', source: 'manual' });
          setSavedPhonePrompt(v.phone_prompt);
          fetchPromptVersionCount();
        } catch {
          // 版本记录失败不影响保存本身
        }
      }
      showNotification({ message: t('save_success'), color: 'green' });
    } catch (error) {
      console.error('保存电话受付设置失败:', error);
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      showNotification({ message: detail || t('phone.save_error'), color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const goIntegration = () => navigate(`/bot/${botId}/settings#publish`);

  /* ---------- 未連携：引导空状态 ---------- */
  if (!loading && !twilioConfig) {
    const features: [ReactNode, string, string][] = [
      [<IconPhone key="d" size={20} color={VIOLET} />, t('phone.feature_direct_title'), t('phone.feature_direct_desc')],
      [
        <IconBook key="k" size={20} color={VIOLET} />,
        t('phone.feature_knowledge_title'),
        t('phone.feature_knowledge_desc'),
      ],
      [
        <IconSparkles key="p" size={20} color={VIOLET} />,
        t('phone.feature_postcall_title'),
        t('phone.feature_postcall_desc'),
      ],
    ];
    return (
      <>
        <ContainerHeader title={t('phone.page_title')} description={t('phone.page_description')} />
        <div className="rounded-xl bg-white mt-6" style={{ boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
          <div
            style={{
              padding: '52px 40px',
              textAlign: 'center',
              background: `radial-gradient(circle at 50% 0%, ${VIOLET_SOFTER}, #fff 70%)`,
            }}
          >
            <div
              style={{
                width: 68,
                height: 68,
                borderRadius: 18,
                background: '#fff',
                border: '1px solid #ECECF1',
                boxShadow: '0 4px 18px rgba(124,104,251,.12)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
              }}
            >
              <IconPhone size={32} color={VIOLET} />
            </div>
            <Text fz={21} fw={700} style={{ letterSpacing: -0.3 }}>
              {t('phone.empty_title')}
            </Text>
            <Text fz={14} c="gray.7" mt={10} maw={460} mx="auto" style={{ lineHeight: 1.7 }}>
              {t('phone.empty_desc')}
            </Text>
            <Group justify="center" mt={26}>
              <Button size="md" leftSection={<IconExternalLink size={18} />} onClick={goIntegration}>
                {t('phone.empty_cta')}
              </Button>
            </Group>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderTop: '1px solid #ECECF1' }}>
            {features.map(([icon, title, desc], i) => (
              <div
                key={title}
                style={{
                  padding: '20px 22px',
                  borderRight: i < 2 ? '1px solid #F1F2F6' : 'none',
                  display: 'flex',
                  gap: 12,
                }}
              >
                {icon}
                <div>
                  <Text fz={13.5} fw={700}>
                    {title}
                  </Text>
                  <Text fz={12} c="gray.6" mt={3} style={{ lineHeight: 1.5 }}>
                    {desc}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  const enabled = form.values.phone_enabled;

  // 基本設定の概要チップ（読み取り専用の状態一覧；編集は各タブで）
  const statusChips: [string, boolean][] = [
    [t('phone.tool_recording_label'), form.values.phone_recording],
    [t('phone.tool_knowledge_label'), form.values.phone_tools.knowledge.enabled],
    [t('phone.tool_booking_label'), form.values.phone_tools.booking.enabled],
    [
      t('phone.post_call_preset_lark_label'),
      form.values.phone_post_call.enabled && form.values.phone_post_call.presets.lark_notify,
    ],
  ];

  // 选项说明 + 推荐项（下拉里显示名称+说明+推荐徽章；输入框只显示名称）
  const voiceDesc: Record<string, string> = {
    ...Object.fromEntries(
      OPENAI_LIVE_ADDITIONAL_VOICE_OPTIONS.map(({ value }) => [value, t(`realtime.voice_characteristics.${value}`)])
    ),
    marin: t('phone.voice_marin'),
    cedar: t('phone.voice_cedar'),
    alloy: t('phone.voice_alloy'),
    ash: t('phone.voice_ash'),
    ballad: t('phone.voice_ballad'),
    coral: t('phone.voice_coral'),
    echo: t('phone.voice_echo'),
    sage: t('phone.voice_sage'),
    shimmer: t('phone.voice_shimmer'),
    verse: t('phone.voice_verse'),
  };
  const modelDesc: Record<string, string> = {
    'gpt-realtime-2': t('phone.model_gpt_realtime_2'),
    'gpt-live-1': t('phone.model_gpt_live_1'),
  };
  const isLiveModel = PHONE_LIVE_MODELS.has(form.values.phone_model);
  const voiceOptions = isLiveModel
    ? [...VOICE_OPTIONS, ...OPENAI_LIVE_ADDITIONAL_VOICE_OPTIONS.map(({ value }) => value)]
    : VOICE_OPTIONS;
  const effortDesc: Record<string, string> = {
    minimal: t('phone.effort_minimal'),
    low: t('phone.effort_low'),
    medium: t('phone.effort_medium'),
  };
  const eagernessDesc: Record<string, string> = {
    low: t('phone.eagerness_low'),
    medium: t('phone.eagerness_medium'),
    high: t('phone.eagerness_high'),
    auto: t('phone.eagerness_auto'),
  };
  const renderOpt =
    (descMap: Record<string, string>, recommended?: string) =>
    ({ option }: { option: { value: string; label: string } }) =>
      (
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text fz={13} fw={500}>
              {option.label}
            </Text>
            {option.value === recommended && (
              <Badge size="xs" color="violet" variant="light">
                {t('phone.recommended')}
              </Badge>
            )}
          </Group>
          {descMap[option.value] && (
            <Text fz={11.5} c="gray.6">
              {descMap[option.value]}
            </Text>
          )}
        </div>
      );

  const isConfigTab = activeTab !== 'log';

  // ── 转人工路由表编辑（form 本地编辑，由底部全局「保存」落库；与文本字段同一 dirty 机制）──
  const pt = form.values.phone_transfer;
  const setTransfer = (patch: Partial<PhoneTransfer>) =>
    form.setFieldValue('phone_transfer', { ...form.values.phone_transfer, ...patch });
  const addTransferRoute = () =>
    setTransfer({
      routes: [
        ...form.values.phone_transfer.routes,
        { id: `r${Date.now()}`, label: '', number: '', intent_examples: [], description: '', enabled: true },
      ],
    });
  const updateTransferRoute = (idx: number, patch: Partial<PhoneRoute>) =>
    setTransfer({ routes: form.values.phone_transfer.routes.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  const removeTransferRoute = (idx: number) =>
    setTransfer({ routes: form.values.phone_transfer.routes.filter((_, i) => i !== idx) });
  const setDefaultRoute = (patch: Partial<PhoneDefaultRoute>) =>
    setTransfer({ default_route: { ...form.values.phone_transfer.default_route, ...patch } });
  // 删除某行，同时关掉可能打开的详情弹框
  const removeRoute = (idx: number) => {
    removeTransferRoute(idx);
    setDetailIdx(null);
  };
  // 当前详情弹框对应的路由
  const detailRoute = detailIdx !== null ? pt.routes[detailIdx] : null;

  // ── 同時通訳（form 本地编辑，底部全局「保存」落库；与转人工同一 dirty 机制）──
  const pi = form.values.phone_interpreter;
  const setInterp = (patch: Partial<PhoneInterpreter>) =>
    form.setFieldValue('phone_interpreter', { ...form.values.phone_interpreter, ...patch });
  // 开关联动着信モード：开=interpreter（来电直接进同传），关=回到 AI 应答
  const setInterpEnabled = (on: boolean) => {
    setInterp({ enabled: on });
    form.setFieldValue('phone_call_mode', on ? 'interpreter' : 'ai');
  };
  // 着信动作三选一互斥（一个 bot 一个号码）：同传模式下 AI 応答相关设置（話術/音声・ツール/転送）
  // 不会被着信使用 → 在这些 tab 顶部显示提示条，避免用户误以为两者同时生效
  const interpModeBanner =
    form.values.phone_call_mode === 'interpreter' ? (
      <Alert color="violet" variant="light" icon={<IconLanguage size={16} />} py={10}>
        <Group justify="space-between" align="center" wrap="wrap" gap={8}>
          <Text fz={12.5} style={{ flex: 1, minWidth: 240 }}>
            {t('phone.interpreter.active_banner')}
          </Text>
          <Button size="compact-xs" variant="subtle" onClick={() => setActiveTab('interpreter')}>
            {t('phone.interpreter.active_banner_link')}
          </Button>
        </Group>
      </Alert>
    ) : null;

  return (
    <>
      <LoadingOverlay visible={loading} />
      <ContainerHeader title={t('phone.page_title')} description={t('phone.page_description')} />

      <Tabs value={activeTab} onChange={setActiveTab} className="mt-6">
        <Tabs.List className="mb-4">
          <Tabs.Tab value="basic">{t('phone.tab_basic')}</Tabs.Tab>
          <Tabs.Tab value="script">{t('phone.tab_script')}</Tabs.Tab>
          <Tabs.Tab value="voice">{t('phone.tab_voice_tools')}</Tabs.Tab>
          <Tabs.Tab value="transfer" leftSection={<IconArrowsRightLeft size={14} />}>
            {t('phone.tab_transfer')}
          </Tabs.Tab>
          <Tabs.Tab
            value="interpreter"
            leftSection={<IconLanguage size={14} />}
            rightSection={
              form.values.phone_interpreter.enabled ? (
                <Badge size="xs" color="green" variant="light">
                  ON
                </Badge>
              ) : undefined
            }
          >
            {t('phone.tab_interpreter')}
          </Tabs.Tab>
          <Tabs.Tab value="log" leftSection={<IconHistory size={14} />}>
            {t('phone.tab_log')}
          </Tabs.Tab>
        </Tabs.List>

        <ScrollArea
          h={
            isConfigTab
              ? 'calc(100vh - var(--mantine-spacing-md) * 14 - 62px)'
              : 'calc(100vh - var(--mantine-spacing-md) * 14)'
          }
        >
          <div className="pr-4 pb-2">
            {/* ============ 基本設定 ============ */}
            <Tabs.Panel value="basic">
              <Stack gap={18}>
                {interpModeBanner}
                {/* 号码概要卡 */}
                <div className="rounded-xl bg-white" style={{ boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
                  <Group gap={16} p="20px 24px" wrap="nowrap">
                    <div
                      style={{
                        width: 46,
                        height: 46,
                        borderRadius: 12,
                        background: enabled ? VIOLET_SOFT : '#F4F4F7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {enabled ? <IconPhone size={22} color={VIOLET} /> : <IconPhoneOff size={22} color="#9AA0AC" />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Group gap={10}>
                        <Text fz={19} fw={700} style={{ fontFamily: 'monospace', letterSpacing: 0.3 }}>
                          {twilioConfig?.phone_number}
                        </Text>
                        <Badge color={enabled ? 'green' : 'gray'} variant="light" size="sm">
                          {enabled ? t('phone.status_active') : t('phone.status_inactive')}
                        </Badge>
                      </Group>
                      <Text fz={12.5} c="gray.6" mt={3}>
                        {robotInfo?.name}
                        {twilioConfig?.created_at
                          ? ` · ${t('phone.linked_date')} ${new Date(twilioConfig.created_at).toLocaleDateString()}`
                          : ''}
                      </Text>
                    </div>
                    <Switch
                      size="md"
                      checked={enabled}
                      onChange={(e) => handleEnabledChange(e.currentTarget.checked)}
                    />
                  </Group>
                  <div
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderTop: '1px solid #F1F2F6' }}
                  >
                    {[
                      { Icon: IconRobot, label: t('phone.bound_bot'), value: robotInfo?.name || '—', on: null },
                      {
                        Icon: IconBook,
                        label: t('phone.tool_knowledge_label'),
                        on: form.values.phone_tools.knowledge.enabled,
                      },
                      { Icon: IconMicrophone, label: t('phone.tool_recording_label'), on: form.values.phone_recording },
                    ].map((it, i) => {
                      const Ico = it.Icon;
                      const off = it.on === false;
                      return (
                        <div
                          key={i}
                          style={{
                            padding: '15px 22px',
                            borderRight: i < 2 ? '1px solid #F1F2F6' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 11,
                          }}
                        >
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 10,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: off ? '#F3F4F6' : VIOLET_SOFT,
                            }}
                          >
                            <Ico size={18} color={off ? '#AEB4BE' : VIOLET} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <Text fz={11.5} c="gray.6" truncate>
                              {it.label}
                            </Text>
                            {it.on === null ? (
                              <Text fz={14} fw={700} mt={1} truncate>
                                {it.value}
                              </Text>
                            ) : (
                              <Group gap={5} mt={3} wrap="nowrap">
                                <span
                                  style={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    background: it.on ? '#12B886' : '#CED4DA',
                                    flexShrink: 0,
                                  }}
                                />
                                <Text fz={13} fw={700} c={it.on ? '#12B886' : 'gray.5'}>
                                  {it.on ? t('phone.status_on') : t('phone.status_off')}
                                </Text>
                              </Group>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Group p="12px 24px" style={{ borderTop: '1px solid #F1F2F6', background: '#FCFCFD' }}>
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      leftSection={<IconExternalLink size={14} />}
                      onClick={goIntegration}
                    >
                      {t('phone.go_integration')}
                    </Button>
                  </Group>
                </div>

                {/* 機能の状態（読み取り専用の概要；編集は各タブ） */}
                <SectionCard icon={<IconRobot size={17} color={VIOLET} />} title={t('phone.section_status')}>
                  <Group gap={10}>
                    {statusChips.map(([label, on]) => (
                      <Badge
                        key={label}
                        size="lg"
                        variant="light"
                        color={on ? 'violet' : 'gray'}
                        leftSection={<span style={{ fontSize: 11 }}>{on ? '●' : '○'}</span>}
                      >
                        {label}
                      </Badge>
                    ))}
                  </Group>
                  <Text fz={12} c="gray.5" mt={10}>
                    {t('phone.status_hint')}
                  </Text>
                </SectionCard>
              </Stack>
            </Tabs.Panel>

            {/* ============ 話術 ============ */}
            <Tabs.Panel value="script">
              <Stack gap={18}>
                {interpModeBanner}
                <SectionCard icon={<IconSparkles size={17} color={VIOLET} />} title={t('phone.section_script')}>
                  <Stack gap="md">
                    <div>
                      <Group justify="space-between" align="center" mb={6}>
                        <Group gap="xs" align="center">
                          <Text size="sm" fw={500}>
                            {t('phone.prompt_label')}
                          </Text>
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            leftSection={<IconClock size={14} />}
                            onClick={() => setHistoryOpened(true)}
                          >
                            {t('settings.prompt.version_history')} ({promptVersionCount})
                          </Button>
                        </Group>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          onClick={() => form.setFieldValue('phone_prompt', DEFAULT_PHONE_PROMPT)}
                        >
                          {t('phone.load_default_template')}
                        </Button>
                      </Group>
                      <Textarea
                        autosize
                        minRows={10}
                        maxRows={22}
                        placeholder={t('phone.prompt_placeholder')}
                        {...form.getInputProps('phone_prompt')}
                      />
                    </div>
                  </Stack>
                </SectionCard>

                <SectionCard icon={<IconBook size={17} color={VIOLET} />} title={t('phone.section_script_extra')}>
                  <Stack gap="md">
                    <Group grow align="flex-start">
                      <TextInput
                        label={t('phone.greeting_label')}
                        description={t('phone.greeting_description')}
                        {...form.getInputProps('phone_greeting')}
                      />
                      <TextInput
                        label={t('phone.farewell_label')}
                        description={t('phone.farewell_description')}
                        {...form.getInputProps('phone_farewell')}
                      />
                    </Group>
                    <Select
                      maw={320}
                      label={t('phone.language_default_label')}
                      description={t('phone.language_default_description')}
                      data={[
                        { value: 'ja', label: '日本語' },
                        { value: 'en', label: 'English' },
                        { value: 'zh', label: '中文' },
                        { value: 'ko', label: '한국어' },
                      ]}
                      value={form.values.phone_language_default}
                      onChange={(val) => val && commit({ phone_language_default: val })}
                    />
                  </Stack>
                </SectionCard>
              </Stack>
            </Tabs.Panel>

            {/* ============ 音声・ツール ============ */}
            <Tabs.Panel value="voice">
              <Stack gap={18}>
                {interpModeBanner}
                <SectionCard icon={<IconMicrophone size={17} color={VIOLET} />} title={t('phone.section_voice')}>
                  <Group grow align="flex-start">
                    <Select
                      label={t('phone.model_label')}
                      description={t('phone.model_field_desc')}
                      data={PHONE_MODEL_OPTIONS}
                      renderOption={renderOpt(modelDesc, PHONE_MODEL_DEFAULT)}
                      value={form.values.phone_model}
                      onChange={(val) =>
                        val &&
                        commit({
                          phone_model: val,
                          phone_voice: resolveRealtimeVoice('openai', val, form.values.phone_voice, 'marin'),
                        })
                      }
                    />
                    <Select
                      label={t('phone.voice_label')}
                      description={t('phone.voice_field_desc')}
                      data={voiceOptions}
                      renderOption={renderOpt(voiceDesc, 'marin')}
                      value={form.values.phone_voice}
                      onChange={(val) => val && commit({ phone_voice: val })}
                    />
                    {/* 推論強度 / 応答の積極性：仅 realtime 系模型可调；全二重模型（gpt-live-1）自理轮次、无 effort */}
                    {!isLiveModel && (
                      <Select
                        label={t('phone.effort_label')}
                        description={t('phone.effort_field_desc')}
                        data={EFFORT_OPTIONS}
                        renderOption={renderOpt(effortDesc, 'low')}
                        value={form.values.phone_effort}
                        onChange={(val) => val && commit({ phone_effort: val })}
                      />
                    )}
                    {!isLiveModel && (
                      <Select
                        label={t('phone.vad_eagerness_label')}
                        description={t('phone.eagerness_field_desc')}
                        data={EAGERNESS_OPTIONS}
                        renderOption={renderOpt(eagernessDesc, 'low')}
                        value={form.values.phone_vad.eagerness}
                        onChange={(val) => val && commit({ phone_vad: { ...form.values.phone_vad, eagerness: val } })}
                      />
                    )}
                  </Group>
                  {isLiveModel && (
                    <Text fz={12} c="gray.6" mt={8}>
                      {t('phone.model_live_hint')}
                    </Text>
                  )}
                  {/* GPT-Live 音声用設定をモデル選択と同じタブにまとめる。業務ルールは話術タブに残す。 */}
                  {isLiveModel && (
                    <Box mt="md">
                      <Group justify="space-between" align="center" mb={6}>
                        <Text size="sm" fw={500}>
                          {t('phone.live_voice_prompt_label')}
                        </Text>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          onClick={() => form.setFieldValue('phone_live_voice_prompt', DEFAULT_LIVE_VOICE_PROMPT)}
                        >
                          {t('phone.load_default_template')}
                        </Button>
                      </Group>
                      <Text fz={12} c="gray.6" mb={6}>
                        {t('phone.live_voice_prompt_desc')}
                      </Text>
                      <Textarea
                        autosize
                        minRows={6}
                        maxRows={18}
                        placeholder={t('phone.live_voice_prompt_placeholder')}
                        {...form.getInputProps('phone_live_voice_prompt')}
                      />
                    </Box>
                  )}
                </SectionCard>

                <SectionCard icon={<IconTool size={17} color={VIOLET} />} title={t('phone.section_tools')}>
                  <SettingRow
                    first
                    title={t('phone.tool_knowledge_label')}
                    description={t('phone.feature_knowledge_desc')}
                    control={
                      <Group gap="sm" wrap="nowrap">
                        {form.values.phone_tools.knowledge.enabled && (
                          <Group gap={6} wrap="nowrap">
                            <Text fz={12} c="gray.6" style={{ whiteSpace: 'nowrap' }}>
                              {t('phone.tool_knowledge_topk')}
                            </Text>
                            <Tooltip label={t('phone.tool_knowledge_topk_hint')} withArrow>
                              <NumberInput
                                w={80}
                                size="xs"
                                min={1}
                                max={20}
                                {...form.getInputProps('phone_tools.knowledge.top_k')}
                                onBlur={() => commit({ phone_tools: form.values.phone_tools })}
                              />
                            </Tooltip>
                          </Group>
                        )}
                        <Switch
                          checked={form.values.phone_tools.knowledge.enabled}
                          onChange={(e) =>
                            commit({
                              phone_tools: {
                                ...form.values.phone_tools,
                                knowledge: { ...form.values.phone_tools.knowledge, enabled: e.currentTarget.checked },
                              },
                            })
                          }
                        />
                      </Group>
                    }
                  />
                  <SettingRow
                    title={t('phone.tool_end_call_label')}
                    description={t('phone.tool_end_call_description')}
                    control={
                      <Switch
                        checked={form.values.phone_tools.end_call.enabled}
                        onChange={(e) =>
                          commit({
                            phone_tools: {
                              ...form.values.phone_tools,
                              end_call: { enabled: e.currentTarget.checked },
                            },
                          })
                        }
                      />
                    }
                  />
                  <SettingRow
                    title={t('phone.tool_recording_label')}
                    description={t('phone.tool_recording_description')}
                    control={
                      <Switch
                        checked={form.values.phone_recording}
                        onChange={(e) => commit({ phone_recording: e.currentTarget.checked })}
                      />
                    }
                  />
                  <SettingRow
                    title={t('phone.tool_transfer_label')}
                    description={t('phone.tool_transfer_description')}
                    control={
                      <Group gap="sm" wrap="nowrap">
                        <Badge size="sm" color={pt.enabled ? 'teal' : 'gray'} variant="light">
                          {pt.enabled ? t('phone.transfer_on') : t('phone.transfer_off')}
                        </Badge>
                        <Button variant="subtle" size="compact-xs" onClick={() => setActiveTab('transfer')}>
                          {t('phone.transfer_configure')}
                        </Button>
                      </Group>
                    }
                  />
                  {/* 予約（Lark カレンダー）：需先在連携アプリ配 Lark 日历应用 */}
                  <SettingRow
                    title={t('phone.tool_booking_label')}
                    description={
                      larkConfig?.app_id ? t('phone.tool_booking_description') : t('phone.tool_booking_need_lark')
                    }
                    disabled={!larkConfig?.app_id}
                    control={
                      <Group gap="sm" wrap="nowrap">
                        {!larkConfig?.app_id && (
                          <Button variant="subtle" size="compact-xs" onClick={goIntegration}>
                            {t('phone.go_integration')}
                          </Button>
                        )}
                        <Switch
                          disabled={!larkConfig?.app_id}
                          checked={form.values.phone_tools.booking.enabled}
                          onChange={(e) =>
                            commit({
                              phone_tools: {
                                ...form.values.phone_tools,
                                booking: { enabled: e.currentTarget.checked },
                              },
                            })
                          }
                        />
                      </Group>
                    }
                  />
                </SectionCard>

                <SectionCard icon={<IconSparkles size={17} color={VIOLET} />} title={t('phone.section_post_call')}>
                  <SettingRow
                    first
                    title={t('phone.post_call_label')}
                    description={t('phone.post_call_description')}
                    control={
                      <Switch
                        checked={form.values.phone_post_call.enabled}
                        onChange={(e) =>
                          commit({
                            phone_post_call: { ...form.values.phone_post_call, enabled: e.currentTarget.checked },
                          })
                        }
                      />
                    }
                  />
                  {form.values.phone_post_call.enabled && (
                    <>
                      {/* ── 分组①：预制后处理（开关即用；后续新预设继续往这块加行） ── */}
                      <div
                        style={{
                          marginLeft: 12,
                          marginTop: 8,
                          padding: '10px 16px 6px',
                          background: '#FAFAFC',
                          borderRadius: 10,
                          borderLeft: `2px solid ${VIOLET_SOFT}`,
                        }}
                      >
                        <Text fz={11.5} fw={700} c="gray.6" style={{ letterSpacing: 0.3 }}>
                          {t('phone.post_call_presets_group')}
                        </Text>
                        <SettingRow
                          first
                          title={t('phone.post_call_preset_notify_label')}
                          description={
                            larkConfig
                              ? t('phone.post_call_preset_notify_description')
                              : t('phone.post_call_preset_notify_need_integration')
                          }
                          disabled={!larkConfig}
                          control={
                            <Group gap="sm" wrap="nowrap">
                              {!larkConfig && (
                                <Button variant="subtle" size="compact-xs" onClick={goIntegration}>
                                  {t('phone.go_integration')}
                                </Button>
                              )}
                              {/* 平台选择：目前仅支持 Lark */}
                              <Select
                                w={110}
                                size="xs"
                                data={[{ value: 'lark', label: 'Lark' }]}
                                value="lark"
                                allowDeselect={false}
                                disabled={!larkConfig}
                                comboboxProps={{ withinPortal: true }}
                              />
                              <Switch
                                disabled={!larkConfig}
                                checked={form.values.phone_post_call.presets.lark_notify}
                                onChange={(e) =>
                                  commit({
                                    phone_post_call: {
                                      ...form.values.phone_post_call,
                                      presets: {
                                        ...form.values.phone_post_call.presets,
                                        lark_notify: e.currentTarget.checked,
                                      },
                                    },
                                  })
                                }
                              />
                            </Group>
                          }
                        />
                      </div>

                      {/* ── 分组②：自定义后处理（与预设独立，可同时使用） ── */}
                      <div
                        style={{
                          marginLeft: 12,
                          marginTop: 10,
                          padding: '10px 16px 16px',
                          background: '#FAFAFC',
                          borderRadius: 10,
                          borderLeft: `2px solid ${VIOLET_SOFT}`,
                        }}
                      >
                        <Text fz={11.5} fw={700} c="gray.6" style={{ letterSpacing: 0.3 }}>
                          {t('phone.post_call_custom_group')}
                        </Text>
                        <Text fz={12} c="gray.6" mt={4} mb={8}>
                          {t('phone.post_call_custom_description')}
                        </Text>
                        <Textarea
                          autosize
                          minRows={3}
                          maxRows={8}
                          placeholder={t('phone.post_call_custom_placeholder')}
                          {...form.getInputProps('phone_post_call.instruction')}
                        />
                      </div>
                    </>
                  )}
                  <Text size="xs" c="gray.5" mt="sm">
                    {t('phone.post_call_hint')}
                  </Text>
                </SectionCard>
              </Stack>
            </Tabs.Panel>

            {/* ============ 着信ログ ============ */}
            {/* ============ 転送（取次 / 转人工意图路由） ============ */}
            <Tabs.Panel value="transfer">
              <Stack gap={18}>
                {interpModeBanner}
                <SectionCard
                  icon={<IconArrowsRightLeft size={17} color={VIOLET} />}
                  title={t('phone.transfer_section')}
                >
                  <SettingRow
                    first
                    title={t('phone.transfer_enable_label')}
                    description={t('phone.transfer_enable_desc')}
                    control={
                      <Switch
                        checked={pt.enabled}
                        onChange={(e) => setTransfer({ enabled: e.currentTarget.checked })}
                      />
                    }
                  />
                  {!twilioConfig && (
                    <Text fz={12.5} c="orange.7" mt={8}>
                      {t('phone.transfer_need_twilio')}
                    </Text>
                  )}
                </SectionCard>

                <SectionCard
                  icon={<IconArrowsRightLeft size={17} color={VIOLET} />}
                  title={t('phone.transfer_routes_title')}
                >
                  <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md" mb="md">
                    <Text fz={12.5} c="gray.6" style={{ flex: 1 }}>
                      {t('phone.transfer_routes_desc')}
                    </Text>
                    <Button
                      variant="light"
                      size="compact-sm"
                      leftSection={<IconPlus size={14} />}
                      onClick={addTransferRoute}
                      style={{ flexShrink: 0 }}
                    >
                      {t('phone.transfer_add_route')}
                    </Button>
                  </Group>

                  {/* 列名仅顶部标一次，与下方数据行同一套网格对齐 */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: TRANSFER_GRID,
                      gap: 14,
                      alignItems: 'center',
                      padding: '0 2px 8px',
                    }}
                  >
                    <Text fz={11} c="gray.5">
                      {t('phone.transfer_route_card_label')}
                    </Text>
                    <Text fz={11} c="gray.5">
                      {t('phone.transfer_number_field')}
                    </Text>
                    <Text fz={11} c="gray.5">
                      {t('phone.transfer_intents_field')}
                    </Text>
                    <Text fz={11} c="gray.5" ta="center">
                      {t('phone.transfer_on')}
                    </Text>
                    <span />
                  </div>

                  {/* 转接对象列表：人名/号码内联编辑；详情收进安静展开；默认转接钉在末行 */}
                  <div style={{ border: '1px solid #ECEEF3', borderRadius: 12, overflow: 'hidden' }}>
                    {pt.routes.length === 0 && (
                      <Text fz={13} c="gray.5" ta="center" py={20}>
                        {t('phone.transfer_routes_empty')}
                      </Text>
                    )}

                    {pt.routes.map((r, idx) => {
                      return (
                        <div
                          key={r.id || idx}
                          style={{
                            borderTop: idx === 0 ? 'none' : '1px solid #F1F2F6',
                            opacity: r.enabled ? 1 : 0.55,
                            padding: 12,
                          }}
                        >
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: TRANSFER_GRID,
                              alignItems: 'center',
                              gap: 14,
                            }}
                          >
                            <TextInput
                              size="xs"
                              placeholder={t('phone.transfer_label_ph')}
                              value={r.label}
                              onChange={(e) => updateTransferRoute(idx, { label: e.currentTarget.value })}
                            />
                            <TextInput
                              size="xs"
                              placeholder="+815012345678"
                              value={r.number}
                              onChange={(e) => updateTransferRoute(idx, { number: e.currentTarget.value })}
                              styles={{ input: { fontVariantNumeric: 'tabular-nums' } }}
                            />
                            <UnstyledButton
                              onClick={() => setDetailIdx(idx)}
                              className="hover:bg-[#F6F7F9]"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                minWidth: 0,
                                height: 30,
                                padding: '0 10px',
                                borderRadius: 6,
                                border: '0.5px solid #ECEEF3',
                                overflow: 'hidden',
                              }}
                            >
                              {r.intent_examples.length > 0 ? (
                                <>
                                  {r.intent_examples.slice(0, 4).map((k) => (
                                    <Badge
                                      key={k}
                                      size="sm"
                                      variant="light"
                                      color="violet"
                                      style={{ flexShrink: 0, textTransform: 'none' }}
                                    >
                                      {k}
                                    </Badge>
                                  ))}
                                  {r.intent_examples.length > 4 && (
                                    <Text fz={11} c="dimmed" style={{ flexShrink: 0 }}>
                                      +{r.intent_examples.length - 4}
                                    </Text>
                                  )}
                                </>
                              ) : r.description.trim() ? (
                                <Text fz={12} c="gray.6" truncate style={{ minWidth: 0 }}>
                                  {r.description}
                                </Text>
                              ) : (
                                <span
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#adb5bd' }}
                                >
                                  <IconPlus size={12} />
                                  <Text fz={12} c="gray.5">
                                    {t('phone.transfer_detail')}
                                  </Text>
                                </span>
                              )}
                            </UnstyledButton>
                            <Switch
                              size="xs"
                              style={{ justifySelf: 'center' }}
                              checked={r.enabled}
                              onChange={(e) => updateTransferRoute(idx, { enabled: e.currentTarget.checked })}
                            />
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              style={{ justifySelf: 'center' }}
                              onClick={() => removeRoute(idx)}
                            >
                              <IconTrash size={15} />
                            </ActionIcon>
                          </div>
                        </div>
                      );
                    })}

                    {/* 默认转接（兜底）：同一套列对齐，加灰「既定」标、不可删除、可关闭 */}
                    <div
                      style={{
                        borderTop: '1px solid #ECEEF3',
                        opacity: pt.default_route.enabled ? 1 : 0.55,
                        padding: 12,
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: TRANSFER_GRID,
                          alignItems: 'center',
                          gap: 14,
                        }}
                      >
                        <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                          <Badge
                            size="sm"
                            color="gray"
                            variant="light"
                            style={{ flexShrink: 0, textTransform: 'none' }}
                          >
                            {t('phone.transfer_default_title')}
                          </Badge>
                          <TextInput
                            size="xs"
                            placeholder={t('phone.transfer_default_label_ph')}
                            value={pt.default_route.label}
                            onChange={(e) => setDefaultRoute({ label: e.currentTarget.value })}
                            style={{ flex: 1, minWidth: 0 }}
                          />
                        </Group>
                        <TextInput
                          size="xs"
                          placeholder="+815012345678"
                          value={pt.default_route.number}
                          onChange={(e) => setDefaultRoute({ number: e.currentTarget.value })}
                          styles={{ input: { fontVariantNumeric: 'tabular-nums' } }}
                        />
                        <span />
                        <Switch
                          size="xs"
                          style={{ justifySelf: 'center' }}
                          checked={pt.default_route.enabled}
                          onChange={(e) => setDefaultRoute({ enabled: e.currentTarget.checked })}
                        />
                        <span />
                      </div>
                      <Text fz={11.5} c="gray.5" mt={8} style={{ lineHeight: 1.5 }}>
                        {t('phone.transfer_default_desc')} {t('phone.transfer_off_hint')}
                      </Text>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={<IconMicrophone size={17} color={VIOLET} />}
                  title={t('phone.transfer_announce_title')}
                >
                  <Textarea
                    autosize
                    minRows={2}
                    label={t('phone.transfer_announce_label')}
                    description={t('phone.transfer_announce_desc')}
                    placeholder={t('phone.transfer_announce_ph')}
                    value={pt.announcement}
                    onChange={(e) => setTransfer({ announcement: e.currentTarget.value })}
                  />
                  {/* 主叫号码字段暂隐藏：默认透传「来电号码」(来电者的号码)，bridge 兜底用本 bot 的 Twilio 号。
                      caller_id 仍保留在配置里（始终空），需要时可再开放。 */}
                  <Textarea
                    mt="md"
                    autosize
                    minRows={2}
                    label={t('phone.transfer_noanswer_label')}
                    description={t('phone.transfer_noanswer_desc')}
                    placeholder={t('phone.transfer_noanswer_ph')}
                    value={pt.no_answer_message}
                    onChange={(e) => setTransfer({ no_answer_message: e.currentTarget.value })}
                  />
                </SectionCard>

                {/* 转接细则弹框：编辑某个转接目标的关键词 + 触发条件（直接写回 form，随底部全局保存落库） */}
                <Modal
                  opened={detailIdx !== null}
                  onClose={() => setDetailIdx(null)}
                  title={detailRoute?.label?.trim() || t('phone.transfer_detail')}
                  centered
                  size="lg"
                >
                  {detailRoute && detailIdx !== null && (
                    <Stack gap="md">
                      <TagsInput
                        label={t('phone.transfer_intents_field')}
                        description={t('phone.transfer_intents_desc')}
                        placeholder={t('phone.transfer_intents_ph')}
                        value={detailRoute.intent_examples}
                        onChange={(val) => updateTransferRoute(detailIdx, { intent_examples: val })}
                      />
                      <Textarea
                        autosize
                        minRows={3}
                        label={t('phone.transfer_desc_field')}
                        description={t('phone.transfer_desc_desc')}
                        placeholder={t('phone.transfer_desc_ph')}
                        value={detailRoute.description}
                        onChange={(e) => updateTransferRoute(detailIdx, { description: e.currentTarget.value })}
                      />
                      <Group justify="flex-end">
                        <Button variant="light" onClick={() => setDetailIdx(null)}>
                          {t('close')}
                        </Button>
                      </Group>
                    </Stack>
                  )}
                </Modal>
              </Stack>
            </Tabs.Panel>

            {/* ============ 同時通訳 ============ */}
            <Tabs.Panel value="interpreter">
              <Stack gap={18}>
                {/* 有効化 */}
                <SectionCard
                  icon={<IconLanguage size={18} color={VIOLET} />}
                  title={t('phone.interpreter.section_enable')}
                >
                  <SettingRow
                    first
                    title={t('phone.interpreter.enable_title')}
                    description={t('phone.interpreter.enable_desc')}
                    control={
                      <Switch
                        checked={pi.enabled}
                        onChange={(e) => setInterpEnabled(e.currentTarget.checked)}
                        size="md"
                      />
                    }
                  />
                  <Text fz={12} c="gray.6" mt={4}>
                    {t('phone.interpreter.call_mode_hint')}
                  </Text>
                </SectionCard>

                {/* オペレーター */}
                <SectionCard
                  icon={<IconPhone size={18} color={VIOLET} />}
                  title={t('phone.interpreter.section_operator')}
                >
                  <Group grow align="flex-start">
                    <TextInput
                      label={t('phone.interpreter.agent_number_label')}
                      description={t('phone.interpreter.agent_number_hint')}
                      placeholder="+84901234567"
                      value={pi.agent_number}
                      onChange={(e) => setInterp({ agent_number: e.currentTarget.value })}
                      styles={{ input: { fontVariantNumeric: 'tabular-nums' } }}
                      withAsterisk={pi.enabled}
                    />
                    <Select
                      label={t('phone.interpreter.agent_language_label')}
                      description={t('phone.interpreter.agent_language_hint')}
                      data={INTERPRETER_LANG_OPTIONS}
                      value={pi.agent_language}
                      onChange={(v) => v && setInterp({ agent_language: v })}
                      allowDeselect={false}
                      searchable
                    />
                  </Group>
                  <Textarea
                    mt="md"
                    label={t('phone.interpreter.no_answer_label')}
                    description={t('phone.interpreter.no_answer_hint')}
                    minRows={2}
                    autosize
                    value={pi.no_answer_message}
                    onChange={(e) => setInterp({ no_answer_message: e.currentTarget.value })}
                  />
                </SectionCard>

                {/* お客様の言語（自動判定） */}
                <SectionCard
                  icon={<IconSparkles size={18} color={VIOLET} />}
                  title={t('phone.interpreter.section_customer')}
                >
                  <Group grow align="flex-start">
                    <MultiSelect
                      label={t('phone.interpreter.candidates_label')}
                      description={t('phone.interpreter.candidates_hint')}
                      data={INTERPRETER_LANG_OPTIONS}
                      value={pi.customer_language_candidates}
                      onChange={(v) => setInterp({ customer_language_candidates: v })}
                      searchable
                      clearable
                    />
                    <Select
                      label={t('phone.interpreter.fallback_lang_label')}
                      description={t('phone.interpreter.fallback_lang_hint')}
                      data={INTERPRETER_LANG_OPTIONS}
                      value={pi.customer_language_default}
                      onChange={(v) => v && setInterp({ customer_language_default: v })}
                      allowDeselect={false}
                      searchable
                    />
                  </Group>
                </SectionCard>

                {/* 音声 */}
                <SectionCard
                  icon={<IconMicrophone size={18} color={VIOLET} />}
                  title={t('phone.interpreter.section_audio')}
                >
                  <SettingRow
                    first
                    title={t('phone.interpreter.ducking_label')}
                    description={t('phone.interpreter.ducking_hint')}
                    control={
                      <Switch
                        checked={pi.original_audio.enabled}
                        onChange={(e) =>
                          setInterp({ original_audio: { ...pi.original_audio, enabled: e.currentTarget.checked } })
                        }
                        size="md"
                      />
                    }
                  />
                  {pi.original_audio.enabled && (
                    <Group align="center" gap={16} mt={6} wrap="nowrap">
                      <Text fz={12.5} c="gray.7" style={{ whiteSpace: 'nowrap' }}>
                        {t('phone.interpreter.ducking_volume')}
                      </Text>
                      <Slider
                        style={{ flex: 1, maxWidth: 320 }}
                        min={0.05}
                        max={0.5}
                        step={0.05}
                        value={pi.original_audio.volume}
                        onChange={(v) => setInterp({ original_audio: { ...pi.original_audio, volume: v } })}
                        label={(v) => `${Math.round(v * 100)}%`}
                      />
                      <Text fz={12.5} c="gray.6" w={40} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {Math.round(pi.original_audio.volume * 100)}%
                      </Text>
                    </Group>
                  )}
                </SectionCard>

                {/* 開始アナウンス */}
                <SectionCard
                  icon={<IconBook size={18} color={VIOLET} />}
                  title={t('phone.interpreter.section_greeting')}
                >
                  <Select
                    label={t('phone.interpreter.greeting_lang_label')}
                    description={t('phone.interpreter.greeting_lang_hint')}
                    data={INTERPRETER_LANG_OPTIONS}
                    value={pi.greeting_language}
                    onChange={(v) => v && setInterp({ greeting_language: v })}
                    allowDeselect={false}
                    searchable
                    maw={320}
                    mb="sm"
                  />
                  <Textarea
                    label={t('phone.interpreter.greeting_label')}
                    description={t('phone.interpreter.greeting_hint')}
                    minRows={2}
                    autosize
                    value={pi.greeting}
                    onChange={(e) => setInterp({ greeting: e.currentTarget.value })}
                  />
                </SectionCard>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="log">
              {botId && <CallLog botId={botId} phoneNumber={twilioConfig?.phone_number} />}
            </Tabs.Panel>
          </div>
        </ScrollArea>

        {/* 保存条：仅文本字段（话术/招呼/邮箱/自定义指示）需要手动保存；开关/下拉已即时保存 */}
        {isConfigTab && (
          <Group justify="flex-end" align="center" gap="md" py={12} pr={16}>
            {textDirty && (
              <Text fz={12.5} c="orange.7">
                {t('phone.unsaved_hint')}
              </Text>
            )}
            <Button miw={140} loading={saving} disabled={!textDirty} onClick={handleSave}>
              {t('phone.save')}
            </Button>
          </Group>
        )}
      </Tabs>

      {/* 电话 prompt 版本历史（与 realtime prompt 同套路） */}
      {botId && (
        <VersionHistoryDrawer
          opened={historyOpened}
          onClose={() => setHistoryOpened(false)}
          aiId={botId}
          promptType="phone"
          currentPrompt={form.values.phone_prompt}
          onVersionActivated={handleVersionActivated}
        />
      )}
    </>
  );
};

export default Phone;
